import { DollarSign, GitCompareArrows, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WaiverMode } from '@/components/mlb-beta/AddDropDialog';

interface AcquisitionStripProps {
  acquisitionsUsed: number;
  maxAcquisitions: number;
  faabRemaining: number;
  waiverMode: WaiverMode;
  compareMode: boolean;
  selectedCount: number;
  onToggleCompare: () => void;
  onConfirmCompare: () => void;
}

const TARGET_COMPARE = 2;
const METER_DOTS = 5;

export function AcquisitionStrip({
  acquisitionsUsed,
  maxAcquisitions,
  faabRemaining,
  waiverMode,
  compareMode,
  selectedCount,
  onToggleCompare,
  onConfirmCompare,
}: AcquisitionStripProps) {
  if (compareMode) {
    const ready = selectedCount >= TARGET_COMPARE;
    const remaining = Math.max(0, TARGET_COMPARE - selectedCount);
    return (
      <div className="flex items-center justify-between gap-2 px-4 py-2 text-[12px]">
        <span className="font-display font-semibold tracking-wide text-foreground truncate">
          {selectedCount} selected
          <span className="mx-1.5 text-muted-foreground">·</span>
          <span className={ready ? 'text-primary' : 'text-muted-foreground'}>
            {ready ? 'ready' : `pick ${remaining} more`}
          </span>
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="ghost" size="sm" onClick={onToggleCompare} className="h-8 px-2.5 text-[12px]">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready}
            onClick={onConfirmCompare}
            className="h-8 px-3 text-[12px] gap-1"
          >
            Compare
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  const filledDots = Math.min(acquisitionsUsed, METER_DOTS);
  const showFaab = waiverMode !== 'fcfs';

  return (
    <div className="flex items-center gap-3 px-4 py-2 text-[12px]">
      {maxAcquisitions > 0 && (
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex items-center gap-1" aria-hidden="true">
            {Array.from({ length: METER_DOTS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  i < filledDots ? 'bg-primary' : 'border border-border',
                )}
              />
            ))}
          </div>
          <span className="font-display font-semibold tracking-wide text-muted-foreground tabular-nums">
            {acquisitionsUsed}/{maxAcquisitions} moves
          </span>
        </div>
      )}

      {showFaab && (
        <div className="flex items-center gap-1 shrink-0 text-gold">
          <DollarSign className="w-3.5 h-3.5" />
          <span className="font-display font-semibold tabular-nums">
            {faabRemaining} FAAB
          </span>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onToggleCompare}
        className="ml-auto h-8 px-2.5 text-[12px] gap-1.5"
      >
        <GitCompareArrows className="w-3.5 h-3.5" />
        Compare
      </Button>
    </div>
  );
}

export default AcquisitionStrip;
